import checkSubVars from "./checkSubVars"
import { AppState } from "../data/redux-state"
import {
  isSpecialCaseSourceKey,
  coVarKeyMainObservation,
  coVarKeyPrimaryTS,
  CoVarOption,
  isMainObservationCoVarKey,
  expandAuxiliaryCoVarKeys,
} from "./extractCoVariables"
import { pickColor } from "./lineUtils"
import { approxEqual } from "./format"
import { IndicatorClass } from "../data/indicator-class"
import { getDerivMode, DerivMode } from "./derivParam"
import { getCumulParams } from "./cumulParam"

function getYearRangeFromURL(): { minYear: number; maxYear: number } {
  const urlParams = new URLSearchParams(window.location.search)
  const minYear = parseInt(
    urlParams.get("minYear") || urlParams.get("minyear") || "1960"
  )
  const maxYear = parseInt(
    urlParams.get("maxYear") || urlParams.get("maxyear") || "2050"
  )
  return { minYear, maxYear }
}

function getValueFilterFromURL(): {
  minValue: number | null
  maxValue: number | null
} {
  if (typeof window === "undefined") {
    return { minValue: null, maxValue: null }
  }
  const urlParams = new URLSearchParams(window.location.search)
  const minRaw = urlParams.get("minValue") || urlParams.get("minvalue")
  const maxRaw = urlParams.get("maxValue") || urlParams.get("maxvalue")
  const minValue =
    minRaw === null || minRaw === "" ? null : parseFloat(minRaw)
  const maxValue =
    maxRaw === null || maxRaw === "" ? null : parseFloat(maxRaw)
  return {
    minValue: Number.isFinite(minValue) ? minValue : null,
    maxValue: Number.isFinite(maxValue) ? maxValue : null,
  }
}

type NamedGeoRegion = {
  geoRegion: GeoRegion
  geoRegionKey: string
  subVarKey: string
}

export interface DPoint {
  date: Date
  value: number
  up?: DPoint
  down?: DPoint
}

export interface UncertaintyLine {
  coVarOption?: CoVarOption
  coVarKey: string
  geoRegionKey: string
  subVarKey: string
  coVar: CoVar
  mainPoints: DPoint[]
  unfilteredPoints: DPoint[] | null // usually null unless if mainPoints need to be filtered
  urPoints: DPoint[] | null
  sDevPoints: DPoint[] | null
  color: string
  cumulRefValue?: number // anchor value (refYear value + RYD) for the cumul legend
}

export type LineType = "PATH_TO_PARIS" | "OBSERVATION" | "OTHER"

function isV3GeoRegion(region: GeoRegion): region is GeoRegionV3 {
  return (
    "PrimaryTS" in region &&
    typeof region.PrimaryTS === "object" &&
    region.PrimaryTS !== null
  )
}

export function getLineType(line: UncertaintyLine): LineType {
  if (line.coVarKey === "p2Paris") {
    return "PATH_TO_PARIS"
  } else if (isMainObservationCoVarKey(line.coVarKey)) {
    return "OBSERVATION"
  } else {
    return "OTHER"
  }
}

export function extractDataLines(appState: AppState): {
  data: UncertaintyLine[]
} {
  const { lines }: { lines: UncertaintyLine[] } = extractRegionsLines(appState)
  const coVarKeys = expandAuxiliaryCoVarKeys(appState.coVarOptions)
  const filteredLines = lines.filter((line) => {
    return coVarKeys.includes(line.coVarKey)
  })
  augmentLinesWithCoVarOptions(filteredLines, appState.coVarOptions)
  sortCoVars(filteredLines, coVarKeys)

  if (appState.indicatorClass !== "CWM-GLO") {
    resolveColorConflicts(filteredLines)
  }
  removeOverlappingPoints(filteredLines)
  removeNonProjectionPoints(filteredLines, appState.indicatorClass)
  applyDerivative(filteredLines)
  applyCumulative(filteredLines)
  applyMinMaxYearFilter(filteredLines)
  applyValueFilter(filteredLines)
  // console.log("filteredLines", filteredLines)
  return { data: filteredLines }
}

function augmentLinesWithCoVarOptions(
  filteredLines: UncertaintyLine[],
  coVarOptions: CoVarOption[]
) {
  for (const line of filteredLines) {
    const option = coVarOptions.find((opt) => opt.value === line.coVarKey)
    if (option) {
      line.coVarOption = option
    } else {
      // For auxiliary group lines, match by prefix (old: CCMean_PrimaryTS) or suffix (new: PrimaryTS_CCMean)
      const groupOption = coVarOptions.find(
        (opt) =>
          opt.auxiliaryGroup &&
          (opt.auxiliaryGroupIsSuffix
            ? line.coVarKey.endsWith("_" + opt.value)
            : line.coVarKey.startsWith(opt.value + "_"))
      )
      if (groupOption) {
        line.coVarOption = groupOption
      } else {
        console.error("Did not find coVarOption for line", line)
      }
    }
  }
}

function sortCoVars(lines: UncertaintyLine[], coVarKeys: string[]) {
  // Extract the order of subVarKeys from the input array
  const subVarOrder = Array.from(new Set(lines.map((line) => line.subVarKey)))

  lines.sort((a, b) => {
    // First, sort by subVarKey based on input order
    const subVarIndexA = subVarOrder.indexOf(a.subVarKey)
    const subVarIndexB = subVarOrder.indexOf(b.subVarKey)
    if (subVarIndexA !== subVarIndexB) {
      return subVarIndexA - subVarIndexB
    }

    // Then, order by coVarKey using the predefined coVarKeysOrder array
    const indexA = coVarKeys.indexOf(a.coVarKey)
    const indexB = coVarKeys.indexOf(b.coVarKey)
    return indexA - indexB
  })
}

function getColorKey(line: UncertaintyLine): string {
  // a unique color should be used for each 2-tuple (geoRegionKey, subVarKey)
  return line.geoRegionKey + "-" + line.subVarKey
}

function resolveColorConflicts(lines: UncertaintyLine[]) {
  const fallbackColors: Record<string, string | undefined> = {}
  const colorToKey: Record<string, string> = {}
  let fallbackIndex: number = 1
  for (const line of lines) {
    const colorKey = getColorKey(line)
    if (!colorToKey[line.color]) {
      // first time we see this color
      colorToKey[line.color] = colorKey
    } else if (colorToKey[line.color] !== colorKey) {
      // color conflict detected
      let fallbackColor = fallbackColors[colorKey]
      if (!fallbackColor) {
        fallbackColor = pickColor(fallbackIndex)
        fallbackIndex += 1
        fallbackColors[colorKey] = fallbackColor
        console.log(
          "Detected a color conflict! Both " +
            colorToKey[line.color] +
            " and " +
            colorKey +
            " are using the same color " +
            line.color +
            ". Using fallback color " +
            fallbackColor +
            " for " +
            colorKey
        )
      }
      line.color = fallbackColor // assign a fallback color to resolve the conflict
    } else {
      // color is already assigned to this colorKey - no need to do anything
    }
  }
}

function removeOverlappingPoints(lines: UncertaintyLine[]) {
  const colorSets: Record<string, UncertaintyLine[]> = {}
  for (const line of lines) {
    const colorKey = getColorKey(line)
    if (!colorSets[colorKey]) {
      colorSets[colorKey] = []
    }
    colorSets[colorKey].push(line)
  }
  for (const colorKey of Object.keys(colorSets)) {
    removeOverlappingPointsForColorSet(colorSets[colorKey])
  }
}

function applyMinMaxYearFilter(lines: UncertaintyLine[]) {
  const { minYear, maxYear } = getYearRangeFromURL()
  if (!minYear && !maxYear) {
    return
  }
  for (const line of lines) {
    line.mainPoints = line.mainPoints.filter((p) => {
      const year = p.date.getFullYear()
      return year >= minYear && year <= maxYear
    })
    line.unfilteredPoints =
      line.unfilteredPoints?.filter((p) => {
        const year = p.date.getFullYear()
        return year >= minYear && year <= maxYear
      }) ?? null
    line.urPoints =
      line.urPoints?.filter((p) => {
        const year = p.date.getFullYear()
        return year >= minYear && year <= maxYear
      }) ?? null
    line.sDevPoints =
      line.sDevPoints?.filter((p) => {
        const year = p.date.getFullYear()
        return year >= minYear && year <= maxYear
      }) ?? null
  }
}

function applyValueFilter(lines: UncertaintyLine[]) {
  const { minValue, maxValue } = getValueFilterFromURL()
  if (minValue === null && maxValue === null) {
    return
  }
  for (const line of lines) {
    const isOutside = (p: DPoint) => {
      if (minValue !== null && p.value < minValue) return true
      if (maxValue !== null && p.value > maxValue) return true
      return false
    }
    // For the line path: replace filtered values with NaN to create gaps
    const linePathPoints = line.unfilteredPoints ?? line.mainPoints
    line.unfilteredPoints = linePathPoints.map(p =>
      isOutside(p) ? { ...p, value: NaN } : p
    )
    // For markers, clickable areas, axis ranges: remove filtered points
    line.mainPoints = line.mainPoints.filter(p => !isOutside(p))
    line.urPoints =
      line.urPoints?.filter(p => !isOutside(p)) ?? null
    line.sDevPoints =
      line.sDevPoints?.filter(p => !isOutside(p)) ?? null
  }
}

// Differentiate each active timeseries along time, per the `deriv` URL parameter.
// Centered: (X[i+1] - X[i-1]) / 2, with forward/backward edges at the ends.
// Forward: X[i] - X[i-1] (change against previous year), first year reuses the
// second year's forward value. See doc/specs/2026-06-30-deriv-url-parameter.md.
function computeDerivativeValues(values: number[], mode: DerivMode): number[] {
  const n = values.length
  if (n < 2) {
    return values.map(() => 0)
  }
  const result = new Array<number>(n)
  if (mode === "forward") {
    for (let i = 1; i < n; i += 1) {
      result[i] = values[i] - values[i - 1]
    }
    result[0] = values[1] - values[0]
  } else {
    for (let i = 1; i < n - 1; i += 1) {
      result[i] = (values[i + 1] - values[i - 1]) / 2.0
    }
    result[0] = values[1] - values[0]
    result[n - 1] = values[n - 1] - values[n - 2]
  }
  return result
}

function applyDerivative(lines: UncertaintyLine[]) {
  const mode = getDerivMode()
  if (mode === null) {
    return
  }
  for (const line of lines) {
    const sorted = [...line.mainPoints].sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    )
    const derived = computeDerivativeValues(
      sorted.map((p) => p.value),
      mode
    )
    line.mainPoints = sorted.map((p, i) => ({ date: p.date, value: derived[i] }))
    // The simple differencing scheme does not define a derivative for the
    // uncertainty bands or the NaN-gap path, so drop them in derivative view.
    line.unfilteredPoints = null
    line.urPoints = null
    line.sDevPoints = null
  }
}

// Integrate each active timeseries along time, per the `cumul` URL parameters.
// Anchor at the reference year r: A = X[r] + RYD; sum forward to the end and
// subtract-down backward to the first year. With inclusive prefix sums S[i] and
// S_ref = sum of values strictly before refYear:
//   C[i] = RYD + S[i] - S_ref   (so C at refYear = X[r] + RYD).
// See doc/specs/2026-06-30-cumul-url-parameter.md.
function applyCumulative(lines: UncertaintyLine[]) {
  const params = getCumulParams()
  if (params === null) {
    return
  }
  for (const line of lines) {
    const sorted = [...line.mainPoints].sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    )
    let sRef = 0
    let refValue = 0
    for (const p of sorted) {
      if (p.date.getFullYear() < params.refYear) {
        sRef += p.value
      } else if (p.date.getFullYear() === params.refYear) {
        refValue = p.value
      }
    }
    let runningSum = 0
    line.mainPoints = sorted.map((p) => {
      runningSum += p.value
      return {
        date: p.date,
        value: params.rydValue + runningSum - sRef,
      }
    })
    line.cumulRefValue = refValue + params.rydValue
    // The simple integration does not define cumulative uncertainty bands or a
    // NaN-gap path, so drop them in cumulative view.
    line.unfilteredPoints = null
    line.urPoints = null
    line.sDevPoints = null
  }
}

function removeNonProjectionPoints(
  lines: UncertaintyLine[],
  indicatorClass: IndicatorClass
) {
  const meanLine = lines.find((l) => l.coVarKey === "andDMean")
  const observationLine = lines.find((l) => isMainObservationCoVarKey(l.coVarKey))
  const projectionLines = lines.filter((l) => l.coVarKey === "andDMeanProj")
  const predictionLines = indicatorClass.startsWith("EWM")
    ? []
    : lines.filter((l) => l.coVarKey === "andAnnPred")
  // The dec.mean projection shares its first (anchor-point year) value with the
  // timeseries it back-connects to: the decadal-mean line (andDMean) in general,
  // or — for CWM data, which has no separate andDMean — the observed 20yr-mean
  // timeseries (the main observation line). When that target is shown, the
  // overlapping anchor marker is already removed by removeOverlappingPoints and
  // the projection line back-connects through it, so keep all points. Only when
  // the target is hidden (projection-only) do we cut the anchor-point year value,
  // so the projection does not start from a value belonging to a hidden
  // observed/20yr-mean timeseries. Applied to every SubVariable's projection.
  const projectionBackConnectTarget = indicatorClass.startsWith("CWM")
    ? observationLine
    : meanLine
  if (!projectionBackConnectTarget) {
    for (const projectionLine of projectionLines) {
      if (projectionLine.mainPoints.length > 0) {
        projectionLine.mainPoints = projectionLine.mainPoints.slice(1)
        // projectionLine.urPoints = projectionLine.urPoints?.slice(1) ?? null
        projectionLine.sDevPoints = projectionLine.sDevPoints?.slice(1) ?? null
      }
    }
  }
  const scenarioLines = lines.filter((l) =>
    l.coVarKey.toLowerCase().includes("scen")
  )
  if (scenarioLines.length && !meanLine) {
    for (const line of scenarioLines) {
      // removeOverlappingPoints may have already removed the handshake point
      // (e.g. GEM Population, where the scenario duplicates the last observed
      // year); only drop the first point if it is still present.
      const overlapAlreadyRemoved =
        line.unfilteredPoints !== null &&
        line.unfilteredPoints.length > line.mainPoints.length
      if (!overlapAlreadyRemoved && line.mainPoints.length > 0) {
        line.mainPoints = line.mainPoints.slice(1)
        line.sDevPoints = line.sDevPoints?.slice(1) ?? null
      }
    }
  }
  if (!observationLine) {
    for (const predictionLine of predictionLines) {
      if (predictionLine.mainPoints.length > 0) {
        predictionLine.mainPoints = predictionLine.mainPoints.slice(1)
        predictionLine.sDevPoints = predictionLine.sDevPoints?.slice(1) ?? null
      }
    }
  }
}

function removeOverlappingPointsForColorSet(lines: UncertaintyLine[]) {
  if (lines.length <= 1) {
    return
  }
  const mainLines = lines.filter(
    (l) => getLineType(l) === "OBSERVATION" || l.coVarKey === "andDMean"
  )
  const otherLines = lines.filter((l) => !mainLines.includes(l))
  for (const line of mainLines) {
    const mainLinePoints: Record<string, DPoint> = {}
    for (const point of line.mainPoints) {
      const year = point.date.getFullYear().toString()
      mainLinePoints[year] = point
    }
    for (const coVarLine of otherLines) {
      removeOverlappingPointsForTwoLines(coVarLine, mainLinePoints)
    }
  }

  const observationLines = lines.filter((l) => getLineType(l) === "OBSERVATION")
  const mainLineUrPoints: Record<string, DPoint> = {}
  for (const line of observationLines) {
    for (const point of line.urPoints ?? []) {
      const year = point.date.getFullYear().toString()
      mainLineUrPoints[year] = point
    }
  }
  for (const coVarLine of otherLines) {
    fillupUrPoint(coVarLine, mainLineUrPoints)
  }
}

function removeOverlappingPointsForTwoLines(
  coVarLine: UncertaintyLine,
  mainLinePoints: Record<string, DPoint>
) {
  if (coVarLine.unfilteredPoints) {
    return // avoid duplicate filterings
  }
  const filteredPoints: DPoint[] = []
  for (const coVarPoint of coVarLine.mainPoints) {
    const year = coVarPoint.date.getFullYear().toString()
    const mainPoint: DPoint | undefined = mainLinePoints[year]
    if (
      mainPoint &&
      mainPoint.value &&
      approxEqual(mainPoint.value, coVarPoint.value)
    ) {
      console.log(
        coVarLine.coVarKey +
          ": remove overlapping point: " +
          JSON.stringify(coVarPoint)
      )
    } else {
      filteredPoints.push(coVarPoint)
    }
  }
  if (filteredPoints.length !== coVarLine.mainPoints.length) {
    coVarLine.unfilteredPoints = coVarLine.mainPoints
    coVarLine.mainPoints = filteredPoints
  }
}

function fillupUrPoint(
  coVarLine: UncertaintyLine,
  mainLineUrPoints: Record<string, DPoint>
) {
  if (
    coVarLine.urPoints &&
    coVarLine.urPoints.length > 0 &&
    coVarLine.unfilteredPoints &&
    coVarLine.unfilteredPoints.length > coVarLine.urPoints.length
  ) {
    const yearBeginUrPoints = coVarLine.urPoints[0].date.getFullYear()
    const yearBefore = yearBeginUrPoints - 1
    const urPoint = mainLineUrPoints[yearBefore.toString()]
    if (urPoint) {
      console.log(
        coVarLine.coVarKey +
          ": Injecting an UR-point from observation data: " +
          JSON.stringify(urPoint)
      )
      coVarLine.urPoints.unshift(urPoint)
    }
  }
}

function extractRegionsLines(appState: AppState): {
  lines: UncertaintyLine[]
} {
  const regions = appState.geoRegion
  const subvariables = appState.subVariable.filter((sv) => !!sv)
  const sourceJson = appState.sourceJson
  if (!sourceJson) {
    return {
      lines: [],
    }
  }
  const mainSubvars: {
    [key: string]: SubVariable
  } = checkSubVars(sourceJson)

  const maxRegions = 6

  const usedRegionObjets: NamedGeoRegion[] = []
  for (const subvar of subvariables) {
    if (usedRegionObjets.length >= maxRegions) {
      break
    }
    for (const region of regions) {
      if (usedRegionObjets.length >= maxRegions) {
        break
      }
      const geoRegion: GeoRegion = mainSubvars[subvar].GeoRegions[region]
      const namedRegion = {
        geoRegion,
        geoRegionKey: region,
        subVarKey: subvar,
      }
      usedRegionObjets.push(namedRegion)
    }
  }

  const lines: UncertaintyLine[] = []
  for (const region of usedRegionObjets) {
    const regionLines = extractLinesFromRegionObject(region)
    lines.push(...regionLines)
  }

  return { lines }
}

function extractLinesFromRegionObject(
  region: NamedGeoRegion
): UncertaintyLine[] {
  const geoRegion = region.geoRegion

  // extract the main sub variable
  const mainData = isV3GeoRegion(geoRegion)
    ? geoRegion.PrimaryTS.Data
    : geoRegion.Data
  const mainMetadata = isV3GeoRegion(geoRegion)
    ? geoRegion.PrimaryTS.Metadata
    : geoRegion.Metadata
  const mainSDevRange = isV3GeoRegion(geoRegion)
    ? geoRegion.PrimaryTS.sDevRange
    : geoRegion.sdevRange?.Data
  const mainP05 = isV3GeoRegion(geoRegion)
    ? geoRegion.PrimaryTS.p05
    : geoRegion.p05?.Data
  const mainP95 = isV3GeoRegion(geoRegion)
    ? geoRegion.PrimaryTS.p95
    : geoRegion.p95?.Data

  const mainSubVar: CoVar = {
    Data: mainData,
    Metadata: mainMetadata,
    sDevRange: mainSDevRange,
    p05: mainP05,
    p95: mainP95,
    GRsSpreadStDev_lower: isV3GeoRegion(geoRegion) ? geoRegion.PrimaryTS.GRsSpreadStDev_lower : undefined,
    GRsSpreadStDev_upper: isV3GeoRegion(geoRegion) ? geoRegion.PrimaryTS.GRsSpreadStDev_upper : undefined,
    GRsSpread90PCI_p05: isV3GeoRegion(geoRegion) ? geoRegion.PrimaryTS.GRsSpread90PCI_p05 : undefined,
    GRsSpread90PCI_p95: isV3GeoRegion(geoRegion) ? geoRegion.PrimaryTS.GRsSpread90PCI_p95 : undefined,
  }
  const lineKeys = {
    geoRegionKey: region.geoRegionKey,
    subVarKey: region.subVarKey,
  }
  const mainLine = extractLineFromCoVar(mainSubVar, {
    ...lineKeys,
    coVarKey: isV3GeoRegion(geoRegion)
      ? coVarKeyPrimaryTS
      : coVarKeyMainObservation,
  })
  const regionLines: UncertaintyLine[] = [mainLine]

  // extract other coVariables if they exist
  const regionWithCoVars: GeoRegionWithCoVars =
    geoRegion as unknown as GeoRegionWithCoVars
  for (const coVarKey of Object.keys(regionWithCoVars)) {
    if (!isSpecialCaseSourceKey(coVarKey) && coVarKey !== "PrimaryTS") {
      const maybeCoVar = regionWithCoVars[coVarKey]
      const coVar: CoVar | undefined =
        maybeCoVar && typeof maybeCoVar === "object" && "Data" in maybeCoVar
          ? (maybeCoVar as CoVar)
          : undefined
      if (coVar?.Data) {
        const coVarLine = extractLineFromCoVar(coVar, {
          ...lineKeys,
          coVarKey,
        })
        regionLines.push(coVarLine)
      }
    }
  }

  // For V3 JSON format: preserve null values as NaN gaps in unfilteredPoints
  if (isV3GeoRegion(geoRegion)) {
    for (const line of regionLines) {
      const data: GeoRegionData = line.coVar.Data
      const hasNull = Object.values(data).some((v) => v === null)
      if (hasNull) {
        line.unfilteredPoints = Object.keys(data).map((key) => ({
          date: new Date(key as any, 0),
          value: data[key] ?? NaN,
        }))
      }
    }
  }

  return regionLines
}

function extractLineFromCoVar(
  coVar: CoVar,
  args: {
    coVarKey: string
    geoRegionKey: string
    subVarKey: string
  }
): UncertaintyLine {
  const mainData: GeoRegionData = coVar.Data
  const mainPoints: DPoint[] = []
  for (const key of Object.keys(mainData)) {
    if (mainData[key] != null) {
      mainPoints.push({
        date: new Date(key as any, 0),
        value: mainData[key]!,
      })
    }
  }

  const { sDevPoints } = extractSdevRangePoints(coVar, mainPoints)
  const { urPoints } = extractURPoints(coVar, mainPoints)

  const metaData = coVar.Metadata
  const line: UncertaintyLine = {
    coVarKey: args.coVarKey,
    geoRegionKey: args.geoRegionKey,
    subVarKey: args.subVarKey,
    coVar,
    mainPoints,
    unfilteredPoints: null,
    urPoints,
    color: metaData.Color,
    sDevPoints,
  }
  return line
}

function extractSdevRangePoints(
  coVar: CoVar,
  mainPoints: DPoint[]
): {
  sDevPoints: DPoint[]
} {
  if (coVar.GRsSpreadStDev_lower && coVar.GRsSpreadStDev_upper) {
    return extractSdevRangePointsV3(coVar, mainPoints)
  }
  if (coVar.AGRMeanStDev_lower && coVar.AGRMeanStDev_upper) {
    return extractSdevRangePointsV3(
      { ...coVar, GRsSpreadStDev_lower: coVar.AGRMeanStDev_lower, GRsSpreadStDev_upper: coVar.AGRMeanStDev_upper },
      mainPoints
    )
  }
  if (coVar.NatVarStDev_lower && coVar.NatVarStDev_upper) {
    return extractSdevRangePointsV3(
      { ...coVar, GRsSpreadStDev_lower: coVar.NatVarStDev_lower, GRsSpreadStDev_upper: coVar.NatVarStDev_upper },
      mainPoints
    )
  }
  if (coVar.AnnValStDev_lower && coVar.AnnValStDev_upper) {
    return extractSdevRangePointsV3(
      { ...coVar, GRsSpreadStDev_lower: coVar.AnnValStDev_lower, GRsSpreadStDev_upper: coVar.AnnValStDev_upper },
      mainPoints
    )
  }
  const sdevMap = extractPointMap(coVar.sDevRange ?? null)

  const sDevPoints: DPoint[] = []

  for (const mainPoint of mainPoints) {
    const year: string = mainPoint.date.getFullYear().toString()
    const sDev = sdevMap[year]
    if (sDev) {
      const up: DPoint = {
        date: mainPoint.date,
        value: mainPoint.value + sDev.value,
      }
      const down: DPoint = {
        date: mainPoint.date,
        value: mainPoint.value - sDev.value,
      }
      const sDevPoint: DPoint = {
        date: mainPoint.date,
        value: mainPoint.value,
        up,
        down,
      }
      sDevPoints.push(sDevPoint)
    }
  }
  return { sDevPoints }
}

function extractSdevRangePointsV3(
  coVar: CoVar,
  mainPoints: DPoint[]
): {
  sDevPoints: DPoint[]
} {
  const lowerMap = extractPointMap(coVar.GRsSpreadStDev_lower ?? null)
  const upperMap = extractPointMap(coVar.GRsSpreadStDev_upper ?? null)
  const sDevPoints: DPoint[] = []
  for (const mainPoint of mainPoints) {
    const year = mainPoint.date.getFullYear().toString()
    const lower = lowerMap[year]
    const upper = upperMap[year]
    if (lower && upper) {
      sDevPoints.push({
        date: mainPoint.date,
        value: mainPoint.value,
        down: { date: mainPoint.date, value: mainPoint.value - lower.value },
        up: { date: mainPoint.date, value: mainPoint.value + upper.value },
      })
    }
  }
  return { sDevPoints }
}

function extractURPoints(
  coVar: CoVar,
  mainPoints: DPoint[]
): {
  urPoints: DPoint[]
} {
  const p05Map = extractPointMap(coVar.p05 ?? coVar.GRsSpread90PCI_p05 ?? coVar.AGRMean90PCI_p05 ?? coVar.NatVar90PCI_p05 ?? null)
  const p095Map = extractPointMap(coVar.p95 ?? coVar.GRsSpread90PCI_p95 ?? coVar.AGRMean90PCI_p95 ?? coVar.NatVar90PCI_p95 ?? null)

  const urPoints: DPoint[] = []

  for (const mainPoint of mainPoints) {
    const year: string = mainPoint.date.getFullYear().toString()
    const po5Point = p05Map[year]
    const p95Point = p095Map[year]
    if (po5Point && p95Point) {
      urPoints.push({
        date: mainPoint.date,
        value: mainPoint.value,
        down: po5Point,
        up: p95Point,
      })
    }
  }
  return { urPoints }
}

function extractPointMap(
  regionData: GeoRegionData | null
): Record<string, DPoint> {
  let points: Record<string, DPoint> = {}
  if (!regionData) {
    return points
  }
  for (const key of Object.keys(regionData)) {
    const value = regionData[key]
    if (value !== null && value !== undefined && typeof value === "number") {
      points[key] = {
        date: new Date(key as any, 0),
        value: value,
      }
    }
  }
  return points
}
